import type { Variant } from "@app/protocol";

/**
 * What the shared table code needs to know about a game, as data (review 0027 B2). One entry per
 * game and the record is exhaustive: a game added to the protocol does not compile until it says
 * these things about itself, and no shared code asks a game for its name.
 */
export interface GameFacts {
  /** Partners across the table: team scores in the bar and at the match end. */
  teams: boolean;
  /** How the match end orders a solo game's seats: as seated, or best first by its own measure. */
  finish: "seated" | "lowest" | "highest";
  /** King: a seat ends the match "out" (zero or above) or "sunk". */
  outOrSunk: boolean;
  /** Seats show how many cards they hold, as little backs behind the avatar (a short hand only). */
  handBacks: boolean;
  /** Cards each seat gets in the opening deal. */
  openingDeal: number;
  /** Baloot deals the rest of the hand once the call is over. */
  dealsRestAfterCall: boolean;
}

const TRICKS_13 = {
  openingDeal: 13,
  dealsRestAfterCall: false,
  outOrSunk: false,
  // Thirteen backs would ring the avatar, and all four hands hold the same number anyway.
  handBacks: false,
} as const;

export const FACTS: Readonly<Record<Variant, GameFacts>> = {
  batak: { ...TRICKS_13, teams: false, finish: "seated" },
  spades: { ...TRICKS_13, teams: true, finish: "seated" },
  tarneeb: { ...TRICKS_13, teams: true, finish: "seated" },
  baloot: {
    teams: true,
    finish: "seated",
    outOrSunk: false,
    handBacks: false,
    openingDeal: 5,
    dealsRestAfterCall: true,
  },
  hearts: { ...TRICKS_13, teams: false, finish: "lowest" },
  king: { ...TRICKS_13, teams: false, finish: "highest", outOrSunk: true },
  // Four at a time, and four more whenever the hands are empty (the `top-up` deal cue).
  pisti: {
    teams: true,
    finish: "seated",
    outOrSunk: false,
    // Four at a time, and a seat may be out of cards while another still holds some.
    handBacks: true,
    openingDeal: 4,
    dealsRestAfterCall: false,
  },
};

/** The facts of a game named on the wire; undefined for a game this build does not know. */
export function factsOf(variant: string | undefined): GameFacts | undefined {
  return variant === undefined ? undefined : (FACTS as Record<string, GameFacts>)[variant];
}
